import type { AccountInfo, PopupRequest, PublicClientApplication } from "@azure/msal-browser";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import { useAuthStore } from "@/store/authStore";
import { useIcs4Store } from "@/store/storeIcs4";
import { useNotificationsStore } from "@/store/notificationsStore";
import { loginRedirect } from "./redirectFallback";
import { setUserAccount, userAzureId, userOid } from "./sessionState";
import type { MsalFlowDeps } from "@ics/auth-api";

const debugPrefix = '[Auth]';

// WI 2211: rapporter nettleser-side innloggingsfeil best-effort. Lazy import (ikke statisk) for å
// unngå sirkulær import-syklus (popupFlow → store → endpoints/authAudit → axiosClient → auth.ts → popupFlow).
function reportLoginFailedBestEffort (payload: { errorCode?: string; reason?: string; provider?: string }): void {
  void import('@/store/authAuditClientStore')
    .then(m => m.useAuthAuditClientStore().reportLoginFailed(payload))
    .catch(() => { /* best-effort */ });
}

// *************************************************
// POPUP BLOKKERING DETEKSJON
// *************************************************

// Reaktiv state for popup-blokkering (eksporteres for bruk i komponenter)
export const isPopupBlocked = ref<boolean>(false);

/**
 * Nullstiller popup-blokkert-signalet (etter vellykket login, eller når bruker
 * ber om nytt popup-forsøk). WI 2448: `isPopupBlocked` er et rent in-memory
 * signal — det finnes ingen localStorage-cache å tømme.
 */
export function resetPopupBlockedCache (): void {
  isPopupBlocked.value = false;
}

// *************************************************
// LOGIN

const loginRequest = {
  prompt: "select_account"
} as PopupRequest;

/**
 * MSAL kaster `ClientConfigurationError` med errorCode `authority_mismatch` når
 * konto-/authority-metadata i localStorage-cachen ikke matcher konfigurert
 * authority (typisk etter en config-/utrullingsendring). Identifiseres robust på
 * errorCode/navn, ikke kun melding.
 */
function isAuthorityMismatch (error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  const errorCode = (error as { errorCode?: string }).errorCode;
  return errorCode === "authority_mismatch"
    || error.name === "ClientConfigurationError"
    || error.message.includes("authority_mismatch");
}

const maxRetries = 3;
const baseDelayMs = 500; // Eksponentiell backoff: 500ms, 1000ms, 2000ms

// True when the MSAL error signals the popup window could not open, or its
// response hash never arrived. Shared by the login and silent-refresh popup fallbacks.
function isPopupWindowError (error: any): boolean {
  return error.name === "BrowserAuthError" &&
    (error.message.includes("popup_window_error") ||
     error.message.includes("hash_empty_error"));
}

// True when the MSAL error is the transient empty-hash error worth retrying with backoff.
function isHashEmptyError (error: any): boolean {
  return error.name === "BrowserAuthError" && error.message.includes("hash_empty_error");
}

// True when the user deliberately dismissed the login popup.
function isUserCancelled (error: any): boolean {
  return error.name === "BrowserAuthError" && error.message.includes("user_cancelled");
}

// Handles a failed loginPopup attempt: authority-mismatch and interaction_in_progress
// cache-clear recovery, transient-error backoff retry, user-cancel and popup-blocked
// fallbacks. Returns the login outcome, or rethrows the original error for login outer
// handler. Extracted verbatim from login inner catch to lower cognitive complexity while
// preserving control flow, listener lifecycle and promise settle order.
async function handleLoginPopupError (
  deps: MsalFlowDeps,
  instance: PublicClientApplication,
  error: any,
  retryCount: number,
  authorityRecoveryAttempted: boolean,
  interactionRecoveryAttempted: boolean
): Promise<boolean> {
  // authority_mismatch (ClientConfigurationError): stale MSAL-state i
  // localStorage etter en config-/utrullingsendring matcher ikke konfigurert
  // authority. Rydd den utdaterte cachen og prøv på nytt ÉN gang automatisk
  // (WI 2191) — ellers låses brukeren ute uten selvbetjent vei tilbake.
  // Speiler interaction_in_progress-recoveryen rett under (clearCache + retry).
  if (isAuthorityMismatch(error) && !authorityRecoveryAttempted) {
    console.warn(`${debugPrefix} authority_mismatch — rydder stale MSAL-cache og prøver på nytt`);
    try {
      await instance.clearCache();
    } catch (clearError) {
      console.warn(`${debugPrefix} clearCache under authority_mismatch-recovery feilet:`, clearError);
    }
    return login(deps, retryCount, true, interactionRecoveryAttempted);
  }

  // interaction_in_progress: et avbrutt/uavsluttet popup-løp etterlater MSALs
  // interaction.status-flagg i localStorage. Flagget overlever en refresh
  // (handleRedirectPromise kortsluttes på no-hash-refresh), og kastes umiddelbart
  // ved neste loginPopup. Backoff hjelper ikke — et persistert flagg ryddes ikke
  // av å vente. Rydd MSAL-cachen og prøv på nytt ÉN gang automatisk (WI 2213),
  // slik authority_mismatch gjør — ellers låses brukeren ute uten selvbetjent vei.
  if (error.name === "BrowserAuthError"
      && error.message.includes("interaction_in_progress")
      && !interactionRecoveryAttempted) {
    console.warn(`${debugPrefix} interaction_in_progress — rydder stale MSAL-interaksjonsstatus og prøver på nytt`);
    try {
      await instance.clearCache();
    } catch (clearError) {
      console.warn(`${debugPrefix} clearCache under interaction_in_progress-recovery feilet:`, clearError);
    }
    return login(deps, retryCount, authorityRecoveryAttempted, true);
  }

  // Forbedret retry-logikk med eksponentiell backoff (hash_empty_error er
  // genuint transient: popup-hash er ikke klar ennå, så å vente hjelper).
  const isRetryableError = isHashEmptyError(error);

  if (isRetryableError && retryCount < maxRetries) {
    const delayMs = baseDelayMs * Math.pow(2, retryCount); // Eksponentiell backoff
    console.warn(`${debugPrefix} Retryable error (${error.message}), retry ${retryCount + 1}/${maxRetries} after ${delayMs}ms`);
    await new Promise(resolve => setTimeout(resolve, delayMs));
    return login(deps, retryCount + 1, authorityRecoveryAttempted, interactionRecoveryAttempted);
  }

  // WI 2448: bruker avbrøt popup-en bevisst — ikke tving en redirect på dem.
  if (isUserCancelled(error)) {
    console.warn(`${debugPrefix} Bruker avbrøt popup-innlogging`);
    return false;
  }

  // WI 2448: popup genuint blokkert (popup_window_error, eller hash_empty_error
  // etter uttømte retries). Fall automatisk tilbake til redirect-flyt — symmetrisk
  // med B2CProvider — i stedet for å stoppe på et banner som krever manuell handling.
  // `isPopupBlocked` settes som in-memory signal (banner/B2C-kortslutning i samme økt).
  if (isPopupWindowError(error)) {
    console.warn(`${debugPrefix} Popup blokkert — faller tilbake til redirect-flyt:`, error.message);
    isPopupBlocked.value = true;
    // WI 2211: rapporter nettleser-side innloggingsfeil (best-effort — svelger egne feil).
    reportLoginFailedBestEffort({ errorCode: 'popup_blocked', reason: error.message, provider: 'entra' });
    await loginRedirect(deps);
    return false;
  }

  setUserAccount(null);
  throw error;
}

export async function login (deps: MsalFlowDeps, retryCount = 0, authorityRecoveryAttempted = false, interactionRecoveryAttempted = false): Promise<boolean> {
  const authStore = useAuthStore();
  const ics4Store = useIcs4Store();
  const { isAuth, userName } = storeToRefs(ics4Store);
  const notificationsStore = useNotificationsStore();

  try {
    // WI 2448: bruk de varme modul-cachene synkront når de finnes, slik at
    // `loginPopup` fyres uten en mellomliggende `await` fra klikk-gesturet
    // (bevarer «user activation»; strenge nettlesere blokkerer ellers popup-en).
    // Async-fallback dekker kald sti (login før boot-warmup er ferdig).
    const instance = deps.getInstanceSync() ?? await deps.getInstance();
    const tokenConfig = deps.getTokenRequestSync() ?? await deps.getTokenRequest();

    try {
      const response = await instance.loginPopup({
        ...loginRequest,
        ...tokenConfig,
        prompt: "select_account"
      });

      if (response?.account) {
        instance.setActiveAccount(response.account);
        userAzureId.value = response.account.username;
        userOid.value = deps.resolveOid(response.account);
        setUserAccount(response.account);

        // Oppdater authStore (ny)
        authStore.setAuthStatus(true, response.account?.name ?? null);

        // Oppdater storeIcs4 for bakoverkompatibilitet
        userName.value = response.account?.name;
        isAuth.value = true;

        // Nullstill popup-blokkert cache ved vellykket login
        resetPopupBlockedCache();

        console.log('Account info:', {
          name: response.account.name,
          username: response.account.username,
          idTokenClaims: response.account.idTokenClaims
        });

        return true;
      }
    } catch (error: any) {
      return await handleLoginPopupError(deps, instance, error, retryCount, authorityRecoveryAttempted, interactionRecoveryAttempted);
    }

    return false;
  } catch (error) {
    console.error("Login error:", error);
    notificationsStore.addError(
      "Innloggingsfeil",
      "Det oppstod en feil under innlogging."
    );
    // WI 2211: rapporter nettleser-side innloggingsfeil (best-effort — svelger egne feil).
    reportLoginFailedBestEffort({
      errorCode: (error as { errorCode?: string })?.errorCode ?? 'login_error',
      reason: (error as { message?: string })?.message ?? String(error),
      provider: 'entra'
    });
    return false;
  }
}

// *************************************************
// LOGOUT
const logoutRequest = {
  mainWindowRedirectUri: "/",
};

export async function logout (deps: MsalFlowDeps): Promise<void> {
  const instance = deps.getInstanceSync();
  if (!instance) {
    throw new Error('MSAL instance ikke initialisert');
  }
  await instance.logoutPopup(logoutRequest);
  await isAuthenticated(deps);
}

// *************************************************
// AUTH SJEKK
//
// Dedupe-staten lever i `deps.flowState` og eies av MSAL-instans-eieren
// (provideren, task 2676) — parallelle sjekker dedupliseres per instans,
// aldri på tvers av providere.

export async function isAuthenticated (deps: MsalFlowDeps): Promise<boolean> {
  const state = deps.flowState;
  if (state.authCheckInProgress && state.lastAuthResult) {
    return state.lastAuthResult;
  }

  state.authCheckInProgress = true;
  state.lastAuthResult = checkAuthentication(deps);

  try {
    return await state.lastAuthResult;
  } finally {
    state.authCheckInProgress = false;
    state.lastAuthResult = null;
  }
}

// Handles a failed silent token refresh in checkAuthentication: short-circuits when
// popup is known blocked, otherwise attempts an interactive popup fallback and marks
// the popup blocked on a popup-related error. Returns the auth outcome and always
// leaves the stores consistent. Extracted verbatim from the silent-refresh catch to
// lower cognitive complexity while preserving control flow and promise settle order.
async function handleSilentRefreshFailure (deps: MsalFlowDeps, instance: PublicClientApplication, account: AccountInfo, silentError: any): Promise<boolean> {
  const authStore = useAuthStore();
  const ics4Store = useIcs4Store();
  const { isAuth } = storeToRefs(ics4Store);

  // Silent refresh feilet - prøv popup som fallback
  console.debug('[Auth] Silent token refresh failed, trying popup fallback:', silentError);

  // Ikke prøv popup hvis vi vet at popup er blokkert
  if (isPopupBlocked.value) {
    console.warn('[Auth] Popup er blokkert - kan ikke bruke popup fallback');
    authStore.setAuthStatus(false, null);
    isAuth.value = false;
    setUserAccount(null);
    return false;
  }

  // Prøv interaktiv popup som fallback
  try {
    const tokenConfig = await deps.getTokenRequest();
    const popupResult = await instance.acquireTokenPopup({
      account: account,
      ...tokenConfig,
      prompt: "select_account"
    });

    if (popupResult?.accessToken) {
      console.log('[Auth] Popup fallback vellykket');
      authStore.setAuthStatus(true, account?.name ?? null);
      isAuth.value = true;
      return true;
    }
  } catch (popupError: any) {
    // Popup også feilet - bruker er ikke autentisert
    console.debug('[Auth] Popup fallback feilet:', popupError);

    // Marker popup som blokkert (in-memory signal) hvis det er en popup-relatert feil
    if (isPopupWindowError(popupError)) {
      isPopupBlocked.value = true;
    }
  }

  authStore.setAuthStatus(false, null);
  isAuth.value = false;
  setUserAccount(null);
  return false;
}

async function checkAuthentication (deps: MsalFlowDeps): Promise<boolean> {
  try {
    const instance = await deps.getInstance();
    const accounts = instance.getAllAccounts();

    const authStore = useAuthStore();
    const ics4Store = useIcs4Store();
    const { isAuth, userName } = storeToRefs(ics4Store);

    if (accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
      userAzureId.value = accounts[0].username;
      userOid.value = deps.resolveOid(accounts[0]);
      userName.value = accounts[0]?.name;
      setUserAccount(accounts[0]);

      // Prøv silent token refresh for å sjekke at token er gyldig
      try {
        const tokenConfig = await deps.getTokenRequest();
        await instance.acquireTokenSilent({
          account: accounts[0],
          ...tokenConfig,
          prompt: "none"
        });
        // Token er gyldig - oppdater begge stores
        authStore.setAuthStatus(true, accounts[0]?.name ?? null);
        isAuth.value = true;

        return true;
      } catch (silentError: any) {
        return await handleSilentRefreshFailure(deps, instance, accounts[0], silentError);
      }
    }
    authStore.setAuthStatus(false, null);
    isAuth.value = false;
    setUserAccount(null);
    return false;
  } catch (error) {
    console.error('Error in isAuthenticated:', error);
    setUserAccount(null);
    return false;
  }
}
